export function validatePassword(password: string) {
  return {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
}

export function passwordStrengthScore(password: string) {
  const rules = validatePassword(password);
  return Object.values(rules).filter(Boolean).length;
}

/**
 * A random password satisfying `validatePassword` above — length 8+, one
 * uppercase, one digit, one special character. Used wherever an account needs
 * a real credential purely to satisfy the platform's create/activate
 * contract, with sign-in actually happening via SSO: invite creation
 * (`users.blocks.api.ts`) and auto-activation (`use-reset-password.ts`). It is
 * never logged, stored, returned, or shown to anyone.
 */
export function generateStrongPassword(): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const digits = "23456789";
  const special = "!@#$%^&*-_";
  const all = upper + lower + digits + special;

  const randomChar = (charset: string) => {
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);
    return charset[bytes[0] % charset.length];
  };

  // One from each required class, then pad to 16 with the full set, then
  // shuffle so the required characters aren't predictably in the first four
  // positions.
  const required = [
    randomChar(upper),
    randomChar(lower),
    randomChar(digits),
    randomChar(special),
  ];
  const rest = Array.from({ length: 12 }, () => randomChar(all));
  const chars = [...required, ...rest];
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}
